import type { PreviewFile, PreviewSource } from "./types";

const extensionMimeMap: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  jfif: "image/jpeg",
  pjpe: "image/pjpeg",
  pjpeg: "image/pjpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  avif: "image/avif",
  jxl: "image/jxl",
  svg: "image/svg+xml",
  bmp: "image/bmp",
  ico: "image/x-icon",
  cur: "image/x-icon",
  tif: "image/tiff",
  tiff: "image/tiff",
  apng: "image/apng",
  heic: "image/heic",
  heif: "image/heif",
  mp4: "video/mp4",
  mpg: "video/mpeg",
  mpeg: "video/mpeg",
  mpe: "video/mpeg",
  mpv: "video/mpv",
  webm: "video/webm",
  ogg: "audio/ogg",
  ogv: "video/ogg",
  mov: "video/quicktime",
  m4v: "video/x-m4v",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
  flv: "video/x-flv",
  wmv: "video/x-ms-wmv",
  "3gp": "video/3gpp",
  "3g2": "video/3gpp2",
  m2ts: "video/mp2t",
  m3u8: "application/vnd.apple.mpegurl",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  aif: "audio/aiff",
  aiff: "audio/aiff",
  aifc: "audio/aiff",
  aac: "audio/aac",
  m4a: "audio/mp4",
  flac: "audio/flac",
  opus: "audio/opus",
  oga: "audio/ogg",
  weba: "audio/webm",
  amr: "audio/amr",
  mid: "audio/midi",
  midi: "audio/midi",
  caf: "audio/x-caf",
  au: "audio/basic",
  snd: "audio/basic",
  wma: "audio/x-ms-wma",
  pdf: "application/pdf",
  epub: "application/epub+zip",
  xps: "application/vnd.ms-xpsdocument",
  oxps: "application/oxps",
  txt: "text/plain",
  lrc: "text/plain",
  log: "text/plain",
  env: "text/plain",
  gitignore: "text/plain",
  dockerignore: "text/plain",
  npmrc: "text/plain",
  yarnrc: "text/plain",
  pnpmrc: "text/plain",
  editorconfig: "text/plain",
  browserslistrc: "text/plain",
  prettierrc: "application/json",
  eslintrc: "application/json",
  stylelintrc: "application/json",
  conf: "text/plain",
  config: "text/plain",
  properties: "text/plain",
  lock: "text/plain",
  json: "application/json",
  jsonc: "application/json",
  json5: "application/json5",
  ipynb: "application/x-ipynb+json",
  jsonl: "application/x-ndjson",
  ndjson: "application/x-ndjson",
  xml: "application/xml",
  yaml: "text/yaml",
  yml: "text/yaml",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  md: "text/markdown",
  markdown: "text/markdown",
  mmd: "text/vnd.mermaid",
  mermaid: "text/vnd.mermaid",
  toml: "application/toml",
  ini: "text/plain",
  proto: "text/x-protobuf",
  tf: "text/x-hcl",
  tfvars: "text/x-hcl",
  hcl: "text/x-hcl",
  tex: "application/x-tex",
  latex: "application/x-tex",
  bib: "text/x-bibtex",
  gv: "text/vnd.graphviz",
  http: "message/http",
  css: "text/css",
  scss: "text/css",
  less: "text/css",
  js: "text/javascript",
  mjs: "text/javascript",
  cjs: "text/javascript",
  ts: "text/typescript",
  tsx: "text/typescript",
  jsx: "text/javascript",
  htm: "text/html",
  html: "text/html",
  vue: "text/plain",
  py: "text/x-python",
  java: "text/x-java-source",
  go: "text/x-go",
  rs: "text/rust",
  rb: "text/x-ruby",
  swift: "text/x-swift",
  kt: "text/x-kotlin",
  kts: "text/x-kotlin",
  scala: "text/x-scala",
  lua: "text/x-lua",
  r: "text/x-r",
  dart: "text/x-dart",
  svelte: "text/plain",
  astro: "text/plain",
  elm: "text/x-elm",
  ex: "text/x-elixir",
  exs: "text/x-elixir",
  clj: "text/x-clojure",
  cljs: "text/x-clojure",
  erl: "text/x-erlang",
  hrl: "text/x-erlang",
  fs: "text/x-fsharp",
  fsx: "text/x-fsharp",
  hs: "text/x-haskell",
  lhs: "text/x-haskell",
  php: "application/x-httpd-php",
  c: "text/x-c",
  cpp: "text/x-c++src",
  h: "text/x-c",
  hpp: "text/x-c++hdr",
  cs: "text/x-csharp",
  sql: "application/sql",
  sh: "application/x-sh",
  bash: "application/x-sh",
  zsh: "application/x-sh",
  fish: "application/x-sh",
  ps1: "text/plain",
  bat: "text/plain",
  cmd: "text/plain",
  dockerfile: "text/plain",
  nginxconf: "text/plain",
  gradle: "text/plain",
  graphql: "application/graphql",
  gql: "application/graphql",
  pem: "application/x-pem-file",
  crt: "application/x-x509-ca-cert",
  cer: "application/pkix-cert",
  ics: "text/calendar",
  vcf: "text/vcard",
  diff: "text/x-diff",
  patch: "text/x-diff",
  geojson: "application/geo+json",
  topojson: "application/topo+json",
  kml: "application/vnd.google-earth.kml+xml",
  kmz: "application/vnd.google-earth.kmz",
  gpx: "application/gpx+xml",
  shp: "application/octet-stream",
  drawio: "application/vnd.jgraph.mxfile",
  dio: "application/vnd.jgraph.mxfile",
  xmind: "application/vnd.xmind.workbook",
  excalidraw: "application/vnd.excalidraw+json",
  tldraw: "application/json",
  zip: "application/zip",
  rar: "application/vnd.rar",
  "7z": "application/x-7z-compressed",
  tar: "application/x-tar",
  gz: "application/gzip",
  tgz: "application/gzip",
  bz2: "application/x-bzip2",
  xz: "application/x-xz",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  docm: "application/vnd.ms-word.document.macroenabled.12",
  dotx: "application/vnd.openxmlformats-officedocument.wordprocessingml.template",
  dotm: "application/vnd.ms-word.template.macroenabled.12",
  dot: "application/msword",
  doc: "application/msword",
  rtf: "application/rtf",
  odt: "application/vnd.oasis.opendocument.text",
  fodt: "application/vnd.oasis.opendocument.text-flat-xml",
  wps: "application/vnd.ms-works",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xltx: "application/vnd.openxmlformats-officedocument.spreadsheetml.template",
  xls: "application/vnd.ms-excel",
  xlt: "application/vnd.ms-excel",
  xlsm: "application/vnd.ms-excel.sheet.macroenabled.12",
  xltm: "application/vnd.ms-excel.template.macroenabled.12",
  xlsb: "application/vnd.ms-excel.sheet.binary.macroenabled.12",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
  fods: "application/vnd.oasis.opendocument.spreadsheet-flat-xml",
  numbers: "application/vnd.apple.numbers",
  et: "application/vnd.ms-excel",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  pptm: "application/vnd.ms-powerpoint.presentation.macroenabled.12",
  ppsx: "application/vnd.openxmlformats-officedocument.presentationml.slideshow",
  ppsm: "application/vnd.ms-powerpoint.slideshow.macroenabled.12",
  potx: "application/vnd.openxmlformats-officedocument.presentationml.template",
  potm: "application/vnd.ms-powerpoint.template.macroenabled.12",
  ppt: "application/vnd.ms-powerpoint",
  pps: "application/vnd.ms-powerpoint",
  odp: "application/vnd.oasis.opendocument.presentation",
  fodp: "application/vnd.oasis.opendocument.presentation-flat-xml",
  key: "application/vnd.apple.keynote",
  dps: "application/vnd.ms-powerpoint",
  eml: "message/rfc822",
  msg: "application/vnd.ms-outlook",
  mbox: "application/mbox",
  ofd: "application/ofd",
  gltf: "model/gltf+json",
  glb: "model/gltf-binary",
  stl: "model/stl",
  obj: "model/obj",
  "3ds": "model/3ds",
  fbx: "application/vnd.autodesk.fbx",
  dae: "model/vnd.collada+xml",
  ply: "application/ply",
  "3mf": "model/3mf",
  usdz: "model/vnd.usdz+zip",
  usdc: "model/vnd.usd",
  usda: "model/vnd.usd",
  usd: "model/vnd.usd",
  wrl: "model/vrml",
  vrml: "model/vrml",
  dxf: "image/vnd.dxf",
  dwg: "application/acad",
  dwf: "model/vnd.dwf",
  step: "model/step",
  stp: "model/step",
  iges: "application/iges",
  igs: "application/iges",
  ifc: "application/x-step",
  sat: "application/sat",
  sab: "application/sab",
  "x_t": "application/x-parasolid",
  "x_b": "application/x-parasolid",
  "3dm": "model/vnd.3dm",
  skp: "application/vnd.sketchup.skp",
  sldprt: "application/sldworks",
  sldasm: "application/sldworks",
  gds: "application/vnd.gds",
  gdsii: "application/x-gdsii",
  oas: "application/vnd.oasis.layout",
  oasis: "application/vnd.oasis.layout",
  ttf: "font/ttf",
  otf: "font/otf",
  woff: "font/woff",
  woff2: "font/woff2",
  eot: "application/vnd.ms-fontobject",
  psd: "image/vnd.adobe.photoshop",
  psb: "image/vnd.adobe.photoshop",
  ai: "application/postscript",
  eps: "application/postscript",
  ps: "application/postscript",
  webarchive: "application/x-webarchive",
  sqlite: "application/vnd.sqlite3",
  sqlite3: "application/vnd.sqlite3",
  db: "application/vnd.sqlite3",
  wasm: "application/wasm",
  parquet: "application/vnd.apache.parquet",
  avro: "application/avro"
};

export async function normalizeFile(
  source: PreviewSource,
  fileName?: string,
  mimeType?: string
): Promise<PreviewFile> {
  if (typeof source === "string") {
    const name = fileName || getFileNameFromUrl(source) || "remote-file";
    const extension = getExtension(name);
    return {
      source,
      name,
      extension,
      mimeType: mimeType || extensionMimeMap[extension] || "",
      url: source
    };
  }

  if (source instanceof File) {
    const extension = getExtension(fileName || source.name);
    return {
      source,
      name: fileName || source.name,
      extension,
      mimeType: mimeType || source.type || extensionMimeMap[extension] || "",
      size: source.size,
      blob: source
    };
  }

  if (source instanceof Blob) {
    const name = fileName || "blob";
    const extension = getExtension(name);
    return {
      source,
      name,
      extension,
      mimeType: mimeType || source.type || extensionMimeMap[extension] || "",
      size: source.size,
      blob: source
    };
  }

  const name = fileName || "buffer";
  const extension = getExtension(name);
  const blob = new Blob([source], { type: mimeType || extensionMimeMap[extension] || "" });
  return {
    source,
    name,
    extension,
    mimeType: blob.type,
    size: blob.size,
    blob
  };
}

function getFileNameFromUrl(source: string): string {
  const rawName = source.split(/[?#]/, 1)[0]?.split("/").filter(Boolean).pop() || "";
  if (!rawName) {
    return "";
  }

  try {
    return decodeURIComponent(rawName);
  } catch {
    return rawName;
  }
}

export function getExtension(name: string): string {
  const clean = name.split("?")[0]?.split("#")[0] || "";
  const index = clean.lastIndexOf(".");
  return index >= 0 ? clean.slice(index + 1).split("!", 1)[0].toLowerCase() : "";
}

export function isTextLike(file: PreviewFile): boolean {
  const textFileName = normalizeFileName(file.name);
  const textFileStem = textFileName.split(".")[0];
  return (
    file.mimeType.startsWith("text/") ||
    [
      "application/json",
      "application/json5",
      "application/x-ipynb+json",
      "application/xml",
      "application/yaml",
      "application/x-yaml",
      "application/sql",
      "application/x-sh",
      "application/x-httpd-php",
      "application/javascript",
      "application/x-javascript",
      "application/typescript",
      "application/x-typescript",
      "application/toml",
      "application/x-toml",
      "application/x-ndjson",
      "application/graphql",
      "application/x-pem-file",
      "application/x-x509-ca-cert",
      "application/pkix-cert",
      "application/x-tex",
      "application/lrc",
      "application/x-lrc",
      "message/http",
      "text/calendar",
      "text/vcard",
      "text/x-bibtex",
      "text/x-hcl",
      "text/x-protobuf",
      "text/vnd.graphviz"
    ].includes(file.mimeType) ||
    [
      "txt",
      "lrc",
      "json",
      "jsonc",
      "json5",
      "ipynb",
      "jsonl",
      "ndjson",
      "xml",
      "yaml",
      "yml",
      "csv",
      "log",
      "env",
      "gitignore",
      "dockerignore",
      "npmrc",
      "yarnrc",
      "pnpmrc",
      "editorconfig",
      "browserslistrc",
      "prettierrc",
      "eslintrc",
      "stylelintrc",
      "conf",
      "config",
      "properties",
      "lock",
      "md",
      "markdown",
      "mmd",
      "mermaid",
      "js",
      "mjs",
      "cjs",
      "ts",
      "tsx",
      "jsx",
      "vue",
      "css",
      "scss",
      "less",
      "html",
      "htm",
      "toml",
      "ini",
      "proto",
      "tf",
      "tfvars",
      "hcl",
      "tex",
      "latex",
      "bib",
      "gv",
      "http",
      "java",
      "py",
      "go",
      "rs",
      "rb",
      "swift",
      "kt",
      "kts",
      "scala",
      "lua",
      "r",
      "dart",
      "svelte",
      "astro",
      "elm",
      "ex",
      "exs",
      "clj",
      "cljs",
      "erl",
      "hrl",
      "fs",
      "fsx",
      "hs",
      "lhs",
      "php",
      "c",
      "cpp",
      "h",
      "hpp",
      "cs",
      "sql",
      "sh",
      "bash",
      "zsh",
      "fish",
      "ps1",
      "bat",
      "cmd",
      "dockerfile",
      "nginxconf",
      "gradle",
      "graphql",
      "gql",
      "pem",
      "crt",
      "cer",
      "ics",
      "vcf",
      "diff",
      "patch"
    ].includes(file.extension) ||
    [
      "dockerfile",
      "makefile",
      "rakefile",
      "gemfile",
      "procfile",
      "jenkinsfile",
      "vagrantfile",
      "brewfile",
      "podfile",
      "go.mod",
      "go.sum",
      "cargo.toml",
      "cargo.lock",
      ".gitignore",
      ".dockerignore",
      ".npmrc",
      ".yarnrc",
      ".pnpmrc",
      ".editorconfig",
      ".browserslistrc",
      ".prettierrc",
      ".eslintrc",
      ".stylelintrc"
    ].includes(textFileName) ||
    [
      "readme",
      "changelog",
      "changes",
      "history",
      "license",
      "licence",
      "copying",
      "notice",
      "authors",
      "contributors",
      "codeowners"
    ].includes(textFileStem)
  );
}

function normalizeFileName(name: string): string {
  const baseName = name.split(/[\\/]/).pop() || name;
  return baseName.toLowerCase();
}
